// FX [MB D8; 03 BR-DM-005]: rates with effective dates and types; documents use the rate effective
// on the document date and snapshot (rate, type, date). `rate`: 1 unit of `from` = rate `to`.
import { z } from 'zod';
import { defineEnum } from '../enums/define.ts';
import type { EnumValue } from '../enums/define.ts';
import { AppError } from '../kernel/errors.ts';
import { businessDateSchema } from '../kernel/date.ts';
import type { BusinessDate } from '../kernel/date.ts';
import { BASE_CURRENCY, currencyCodeSchema, minorUnitsOf } from './currency.ts';
import type { CurrencyCode } from './currency.ts';
import { formatDecimal, parseDecimal, pow10, roundHalfEven, toSafeNumber } from './decimal.ts';
import { money } from './money.ts';
import type { Money } from './money.ts';

export const FX_RATE_TYPE = defineEnum('masters.fx_rate.rate_type', [
  'transaction',
  'costing',
  'month_end',
  'average',
]);
export type FxRateType = EnumValue<typeof FX_RATE_TYPE>;

/** numeric(18,8). */
export const FX_SCALE = 8;
const FX_ONE = pow10(FX_SCALE);

export const fxRateDecimalSchema = z
  .string()
  .regex(/^\d{1,10}(\.\d{1,8})?$/)
  .refine((v) => parseDecimal(v, FX_SCALE) > 0n, { message: 'rate must be positive' });

export interface FxRate {
  readonly from: CurrencyCode;
  readonly to: CurrencyCode;
  readonly rate: string;
  readonly rateType: FxRateType;
  readonly effectiveDate: BusinessDate;
}

export const fxRateSchema = z.object({
  from: currencyCodeSchema,
  to: currencyCodeSchema,
  rate: fxRateDecimalSchema,
  rateType: FX_RATE_TYPE.schema,
  effectiveDate: businessDateSchema,
});

/** What a document stores (`fx_rate`, `fx_rate_type`, `fx_rate_date`). */
export interface FxSnapshot {
  readonly fxRate: string;
  readonly fxRateType: FxRateType;
  readonly fxRateDate: BusinessDate;
}

export const fxSnapshotSchema = z.object({
  fxRate: fxRateDecimalSchema,
  fxRateType: FX_RATE_TYPE.schema,
  fxRateDate: businessDateSchema,
});

export function snapshotOf(rate: FxRate): FxSnapshot {
  return Object.freeze({
    fxRate: rate.rate,
    fxRateType: rate.rateType,
    fxRateDate: rate.effectiveDate,
  });
}

/** Identity snapshot for base-currency documents. */
export function baseSnapshot(rateType: FxRateType, onDate: BusinessDate): FxSnapshot {
  return Object.freeze({
    fxRate: formatDecimal(FX_ONE, FX_SCALE),
    fxRateType: rateType,
    fxRateDate: onDate,
  });
}

/**
 * Picks the rate effective on `onDate` (latest effective date ≤ onDate) for one type and pair;
 * none → 422 FX_RATE_MISSING (never silently 1.0).
 */
export function pickRate(
  rates: readonly FxRate[],
  query: {
    readonly from: CurrencyCode;
    readonly to?: CurrencyCode;
    readonly rateType: FxRateType;
    readonly onDate: BusinessDate;
  },
): FxRate {
  const to = query.to ?? BASE_CURRENCY;
  let best: FxRate | undefined;
  for (const r of rates) {
    if (r.from !== query.from || r.to !== to || r.rateType !== query.rateType) continue;
    if (r.effectiveDate > query.onDate) continue;
    if (!best || r.effectiveDate > best.effectiveDate) best = r;
  }
  if (!best) {
    throw new AppError('FX_RATE_MISSING', {
      messageKey: 'errors.units.fxRateMissing',
      params: { from: query.from, to, rateType: query.rateType, onDate: query.onDate },
    });
  }
  return best;
}

/** Converts `amount` with a decimal rate into `to`, half-even to the target minor unit. */
export function convertMoneyAt(amount: Money, rate: string, to: CurrencyCode): Money {
  const r = parseDecimal(rate, FX_SCALE);
  if (r <= 0n) throw new RangeError('rate must be positive');
  const num = BigInt(amount.amountMinor) * r * pow10(minorUnitsOf(to));
  const den = FX_ONE * pow10(minorUnitsOf(amount.currency));
  return money(toSafeNumber(roundHalfEven(num, den)), to);
}

export function convertMoney(amount: Money, rate: FxRate): Money {
  if (amount.currency !== rate.from) {
    throw new AppError('CURRENCY_MISMATCH', {
      messageKey: 'errors.units.currencyMismatch',
      params: { left: amount.currency, right: rate.from },
    });
  }
  return convertMoneyAt(amount, rate.rate, rate.to);
}

/** Base-currency (CAD) equivalent using a document's snapshot; CAD amounts pass through. */
export function toBase(amount: Money, snapshot: FxSnapshot): Money {
  if (amount.currency === BASE_CURRENCY) return amount;
  return convertMoneyAt(amount, snapshot.fxRate, BASE_CURRENCY);
}
